import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, ArrowUpRight, CreditCard, FileText, User,
  AlertTriangle, Check, Download, Save
} from 'lucide-react';

import { API, RAZORPAY_KEY_ID } from '@/config';

const PLAN_ORDER = ['free', 'single', 'starter', 'pro', 'business'];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [downgrading, setDowngrading] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  // Billing details form
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingCompany, setBillingCompany] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingGst, setBillingGst] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/billing`),
        axios.get(`${API}/plans`),
      ]);
      setBilling(b.data);
      setPlans(p.data);
      const d = b.data.billing_details || {};
      setBillingName(d.billing_name || '');
      setBillingEmail(d.billing_email || '');
      setBillingCompany(d.billing_company || '');
      setBillingAddress(d.billing_address || '');
      setBillingGst(d.billing_gst || '');
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); loadRazorpayScript(); }, [loadData]);

  const saveBillingDetails = async () => {
    setSavingDetails(true);
    try {
      await axios.put(`${API}/billing/details`, {
        billing_name: billingName,
        billing_email: billingEmail,
        billing_company: billingCompany,
        billing_address: billingAddress,
        billing_gst: billingGst,
      });
      toast.success('Billing details saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription?\n\nYour current plan features remain active until the end of this billing period.\nNo refunds for the current month.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`);
      await refreshUser();
      toast.success('Subscription cancelled. Access continues until end of billing period.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleDowngrade = useCallback(async (targetPlan) => {
    const targetName = plans[targetPlan]?.name || targetPlan;
    if (!window.confirm(`Downgrade to ${targetName}?\n\nCurrent plan stays active until end of billing period.\nNo refunds for the current month.`)) return;
    setDowngrading(targetPlan);
    try {
      try { await axios.post(`${API}/subscriptions/cancel`); } catch (e) { /* May have no active sub */ }
      if (targetPlan === 'free') {
        await refreshUser();
        toast.success('Downgraded to Free.');
        loadData();
      } else {
        const res = await axios.post(`${API}/subscriptions/create`, { plan: targetPlan });
        await loadRazorpayScript();
        const options = {
          key: RAZORPAY_KEY_ID,
          subscription_id: res.data.subscription_id,
          name: 'Whisper.AI',
          description: `${res.data.plan_name} Plan`,
          handler: async (response) => {
            try {
              await axios.post(`${API}/subscriptions/verify`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
              });
              await refreshUser();
              toast.success(`Switched to ${res.data.plan_name}!`);
              loadData();
            } catch { toast.error('Payment verification failed'); }
            setDowngrading(null);
          },
          modal: { ondismiss: () => setDowngrading(null) },
          prefill: { name: billingName || user?.name, email: billingEmail || user?.email },
          theme: { color: '#10B981' },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => { toast.error('Payment failed'); setDowngrading(null); });
        rzp.open();
        return;
      }
    } catch {
      toast.error('Failed to downgrade');
    } finally {
      setDowngrading(null);
    }
  }, [plans, user, billingName, billingEmail, refreshUser]);

  const currentPlanIndex = PLAN_ORDER.indexOf(billing?.plan || 'free');
  const lowerPlans = PLAN_ORDER.slice(0, currentPlanIndex).filter(p => p !== billing?.plan);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading billing...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      <header className="border-b border-border px-6 py-3 flex items-center gap-3">
        <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-sm font-semibold text-foreground">Billing & Subscription</h1>
          <p className="text-xs text-muted-foreground">Manage your plan, invoices, and billing details</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <Tabs defaultValue="subscription" data-testid="billing-tabs">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="subscription" data-testid="tab-subscription">
              <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Plan
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Invoices
            </TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">
              <User className="h-3.5 w-3.5 mr-1.5" /> Details
            </TabsTrigger>
          </TabsList>

          {/* ─── Subscription Tab ─── */}
          <TabsContent value="subscription">
            <div className="space-y-5" data-testid="subscription-section">
              {/* Current plan card */}
              <div className="p-5 rounded-xl border border-border bg-card">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Current Plan</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{billing?.plan_name || 'Free'}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {billing?.plan_price > 0 ? `$${billing.plan_price}/month` : 'No charge'}
                      {' · '}{billing?.plan_details?.messages_limit?.toLocaleString()} messages/mo
                      {' · '}{billing?.plan_details?.widgets_limit === 999 ? 'Unlimited' : billing?.plan_details?.widgets_limit} chat bot{billing?.plan_details?.widgets_limit !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <Link to="/pricing">
                    <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white gap-1" data-testid="change-plan-btn">
                      <ArrowUpRight className="h-3 w-3" /> Change Plan
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Active subscription details */}
              {billing?.subscription && (
                <div className="p-4 rounded-xl border border-border bg-card/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full ${billing.subscription.status === 'active' ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                    <p className="text-sm font-medium text-foreground capitalize">Subscription {billing.subscription.status}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium text-foreground">Subscription ID</span>
                      <p className="mt-0.5 font-mono">{billing.subscription.subscription_id}</p>
                    </div>
                    {billing.subscription.activated_at && (
                      <div>
                        <span className="font-medium text-foreground">Active since</span>
                        <p className="mt-0.5">{new Date(billing.subscription.activated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      </div>
                    )}
                    {billing.subscription.payment_id && (
                      <div>
                        <span className="font-medium text-foreground">Last Payment</span>
                        <p className="mt-0.5 font-mono">{billing.subscription.payment_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Downgrade / Cancel */}
              {billing?.plan !== 'free' && (
                <div className="p-5 rounded-xl border border-border bg-card space-y-4">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Downgrade or Cancel</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Changes take effect at the end of your current billing period. <strong className="text-foreground">No refunds for the current month.</strong> You'll keep full access to your current plan until then.
                      </p>
                    </div>
                  </div>

                  {lowerPlans.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Downgrade to:</p>
                      <div className="flex flex-wrap gap-2">
                        {lowerPlans.map(planKey => {
                          const p = plans[planKey];
                          if (!p) return null;
                          return (
                            <Button key={planKey} size="sm" variant="outline" onClick={() => handleDowngrade(planKey)} disabled={downgrading === planKey} data-testid={`downgrade-to-${planKey}`}>
                              {downgrading === planKey ? 'Processing...' : `${p.name}${p.price > 0 ? ` ($${p.price}/mo)` : ''}`}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelling} data-testid="cancel-subscription-btn">
                    {cancelling ? 'Cancelling...' : 'Cancel Subscription & Switch to Free'}
                  </Button>
                </div>
              )}

              {billing?.plan === 'free' && (
                <div className="p-5 rounded-xl border border-dashed border-border text-center">
                  <p className="text-sm text-muted-foreground">You're on the Free plan.</p>
                  <Link to="/pricing" className="text-sm text-[#10B981] hover:underline inline-flex items-center gap-1 mt-2 font-medium">
                    Upgrade your plan <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── Invoices Tab ─── */}
          <TabsContent value="invoices">
            <div data-testid="invoices-section">
              {(!billing?.invoices || billing.invoices.length === 0) ? (
                <div className="text-center py-16 border border-dashed border-border rounded-xl">
                  <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">No invoices yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Invoices will appear here after your first payment.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <div className="col-span-3">Date</div>
                    <div className="col-span-3">Plan</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Download</div>
                  </div>
                  {billing.invoices.map((inv) => (
                    <div key={inv.invoice_id} className="grid grid-cols-12 gap-2 px-4 py-3 rounded-lg border border-border bg-card items-center" data-testid={`invoice-${inv.invoice_id}`}>
                      <div className="col-span-3 text-sm text-foreground">
                        {new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div className="col-span-3 text-sm text-foreground font-medium">
                        {inv.plan_name}
                      </div>
                      <div className="col-span-2 text-sm text-foreground font-semibold">
                        ${inv.amount_usd}
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 'bg-zinc-100 text-zinc-700'
                        }`}>
                          {inv.status === 'paid' && <Check className="h-3 w-3" />}
                          {inv.status}
                        </span>
                      </div>
                      <div className="col-span-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 text-xs"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/invoices/${inv.invoice_id}/pdf`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `whisperai-invoice-${inv.invoice_id}.pdf`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch { toast.error('Failed to download invoice'); }
                          }}
                          data-testid={`download-invoice-${inv.invoice_id}`}
                        >
                          <Download className="h-3 w-3" /> PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── Billing Details Tab ─── */}
          <TabsContent value="details">
            <div className="space-y-5" data-testid="billing-details-section">
              <div className="p-5 rounded-xl border border-border bg-card">
                <p className="text-sm font-semibold text-foreground mb-1">Billing Information</p>
                <p className="text-xs text-muted-foreground mb-5">This information will appear on your invoices.</p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="billingName" className="text-xs">Full Name / Company Name <span className="text-destructive">*</span></Label>
                      <Input id="billingName" value={billingName} onChange={(e) => setBillingName(e.target.value)} placeholder="John Doe or Acme Inc." data-testid="billing-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="billingEmail" className="text-xs">Billing Email</Label>
                      <Input id="billingEmail" type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="billing@company.com" data-testid="billing-email-input" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="billingCompany" className="text-xs">Company Name (optional)</Label>
                    <Input id="billingCompany" value={billingCompany} onChange={(e) => setBillingCompany(e.target.value)} placeholder="Acme Inc." data-testid="billing-company-input" />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="billingAddress" className="text-xs">Billing Address</Label>
                    <Input id="billingAddress" value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="123 Main St, City, Country" data-testid="billing-address-input" />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="billingGst" className="text-xs">GST / Tax ID (optional)</Label>
                    <Input id="billingGst" value={billingGst} onChange={(e) => setBillingGst(e.target.value)} placeholder="GSTIN or Tax ID" data-testid="billing-gst-input" />
                  </div>

                  <Button onClick={saveBillingDetails} disabled={savingDetails || !billingName.trim()} className="w-full gap-1.5" data-testid="save-billing-details-btn">
                    <Save className="h-4 w-4" /> {savingDetails ? 'Saving...' : 'Save Billing Details'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
